import React, { useEffect, useMemo, useState } from 'react';
import { Calendar as CalIcon, ChevronLeft, ChevronRight, MapPin, User, Bell, BellRing } from 'lucide-react';
import { Button } from '../ui/button';
import { mccGet, mccPost } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, StatusPill, PlatformIcon, Avatar } from './mccUi';

// Simple month calendar grid for filming sessions.
const monthLabel = (d) => d.toLocaleString('en-AU', { month: 'long', year: 'numeric' });

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

export default function MccFilmingCalendar({ team }) {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try {
      const from = startOfMonth(cursor).toISOString().slice(0, 10);
      const to = endOfMonth(cursor).toISOString().slice(0, 10);
      const res = await mccGet('/marketing/filming-calendar', { from_date: from, to_date: to });
      setItems(res.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load calendar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor.getTime()]);

  const grid = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const startDow = start.getDay();
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= end.getDate(); d++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [cursor]);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      const key = (it.filming_date || '').slice(0, 10);
      if (!key) continue;
      (map[key] = map[key] || []).push(it);
    }
    return map;
  }, [items]);

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Filming Calendar"
        subtitle="Upcoming filming sessions across the month — who's being filmed, where and when."
        icon={CalIcon}
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm"
              onClick={async () => {
                try {
                  const res = await mccPost('/marketing/filming/auto-remind-upcoming', {});
                  if (res.count === 0) toast.info('Nobody needed reminding — everyone up to date.');
                  else toast.success(`Sent ${res.count} filming reminder${res.count === 1 ? '' : 's'}`);
                } catch (e) { toast.error(e.message || 'Reminder run failed'); }
              }}
              data-testid="mcc-filming-auto-remind"
            ><BellRing className="w-3 h-3 mr-1" /> Remind next 24h</Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, -1))}><ChevronLeft className="w-3 h-3" /></Button>
            <p className="text-sm font-bold text-slate-900 w-36 text-center tabular-nums">{monthLabel(cursor)}</p>
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, 1))}><ChevronRight className="w-3 h-3" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(startOfMonth(new Date()))}>Today</Button>
          </div>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-7 border-b bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="px-2 py-2 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[110px] bg-slate-50 border-b border-r border-slate-100" />;
            const key = d.toISOString().slice(0, 10);
            const list = byDay[key] || [];
            const isToday = key === new Date().toISOString().slice(0, 10);
            return (
              <div key={i} className="min-h-[110px] border-b border-r border-slate-100 p-1.5 relative">
                <div className="text-[11px] font-bold text-slate-500 flex items-center justify-between">
                  <span className={isToday ? 'text-white bg-indigo-600 w-5 h-5 rounded-full inline-flex items-center justify-center' : ''}>{d.getDate()}</span>
                </div>
                <div className="mt-1 space-y-1">
                  {list.slice(0, 3).map((f) => (
                    <div key={f.id} className="rounded bg-violet-100 border-l-2 border-violet-600 px-1.5 py-1 text-[10px]">
                      <p className="font-bold text-violet-900 truncate">{f.title}</p>
                      <p className="text-violet-800 truncate">
                        {f.filming_time ? `${f.filming_time} · ` : ''}{f.person_being_filmed || ''}
                      </p>
                    </div>
                  ))}
                  {list.length > 3 && <p className="text-[10px] text-slate-500">+{list.length - 3} more</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming details */}
      <div>
        <p className="text-sm font-bold text-slate-800 mb-2">{loading ? 'Loading…' : `${items.length} sessions this month`}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((f) => (
            <div key={f.id} className="border border-slate-200 rounded-xl bg-white p-3 hover:shadow-md">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold text-violet-700 uppercase">{new Date(f.filming_date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                {f.filming_time ? <span className="text-[10px] text-slate-500">{f.filming_time}</span> : null}
                <span className="ml-auto"><StatusPill status={f.status} size="sm" /></span>
              </div>
              <p className="text-sm font-bold text-slate-900">{f.title}</p>
              <div className="mt-2 space-y-1 text-[11px] text-slate-600">
                {f.person_being_filmed && <p className="flex items-center gap-1"><User className="w-3 h-3" /> {f.person_being_filmed}</p>}
                {f.location && <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {f.location}</p>}
              </div>
              <div className="flex items-center gap-1 mt-2">
                {f.primary_platform && <PlatformIcon platform={f.primary_platform} size={18} />}
                {(f.additional_platforms || []).slice(0, 3).map((p) => <PlatformIcon key={p} platform={p} size={18} />)}
                {f.responsible_filmer_id && <span className="ml-auto"><Avatar name={nameOf(f.responsible_filmer_id)} size={20} /></span>}
              </div>
              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                {f.last_reminder_at ? (
                  <span>Last reminded {new Date(f.last_reminder_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                ) : (
                  <span className="italic">No reminder sent yet</span>
                )}
                <button
                  onClick={async () => {
                    try {
                      const res = await mccPost(`/marketing/filming/${f.id}/send-reminder`, {});
                      toast.success(`Reminded ${res.recipients} team member${res.recipients === 1 ? '' : 's'}`);
                      load();
                    } catch (e) { toast.error(e.message || 'Reminder failed'); }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
                  data-testid={`mcc-filming-remind-${f.id}`}
                ><Bell className="w-3 h-3" /> Send reminder</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
